import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, Paper } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';

import './App.css';
import { theme } from './theme';
import { trackPageView } from './utils/analytics';
import SEOHead from './components/SEOHead';
import StructuredData from './components/StructuredData';
import TopNav from './components/composed/topNav/TopNav';
import Footer from './components/composed/footer/Footer';
import MainPage from './components/composed/mainPage/MainPage';
import ProfessionalBackground from './components/composed/professionalBackground/ProfessionalBackground';
import GetToKnowMe from './components/composed/getToKnowMe/GetToKnowMe';
import Endorsements from './components/composed/endorsements/Endorsements';
import Hangman from './components/composed/widgets/hangman/Hangman';
import ContactMe from './components/composed/contactMe/ContactMe';

const pages = [
  { title: 'About', slug: 'about', path: '/about' },
  { title: 'Professional Background', slug: 'experience', path: '/experience' },
  { title: 'Endorsements', slug: 'endorsements', path: '/endorsements' },
  { title: 'Widgets', slug: 'widgets', path: '/widgets' },
  { title: 'Contact Me', slug: 'contact', path: '/contact' },
];

function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    const pageTitle =
      location.pathname === '/'
        ? 'Home'
        : pages.find(p => p.path === location.pathname)?.title || 'Unknown';
    trackPageView(pageTitle, location.pathname);

    // Move focus to main content on route change for screen readers
    setTimeout(() => {
      const mainContent = document.getElementById('main-content');
      if (mainContent) {
        mainContent.focus();
        // Remove focus after a moment to avoid visible focus ring, but screen reader will have announced it
        setTimeout(() => {
          mainContent.blur();
        }, 100);
      }
    }, 100);
  }, [location]);

  return null;
}

function SkipLinks() {
  return (
    <div className="skip-links-container">
      <a
        href="#main-content"
        className="skip-link"
        onFocus={e => {
          e.target.style.left = '8px';
        }}
        onBlur={e => {
          e.target.style.left = '-9999px';
        }}
      >
        Skip to main content
      </a>
      <a
        href="#main-navigation"
        className="skip-link skip-link-nav"
        onFocus={e => {
          e.target.style.left = '208px';
        }}
        onBlur={e => {
          e.target.style.left = '-9999px';
        }}
      >
        Skip to navigation
      </a>
    </div>
  );
}

function AppContent() {
  return (
    <div className="App">
      <SEOHead />
      <StructuredData />
      <SkipLinks />
      <ThemeProvider theme={theme}>
        <TopNav pages={pages} />
        <Paper
          className="PaperLayout"
          elevation={3}
          component="main"
          id="main-content"
          role="main"
          tabIndex="-1"
        >
          <ErrorBoundary
            FallbackComponent={Fallback}
            onReset={() => {
              // Reset the state of your app so the error doesn't happen again
            }}
          >
            <Routes>
              <Route path="/" element={<MainPage />} />
              <Route path="/about" element={<GetToKnowMe />} />
              <Route path="/experience" element={<ProfessionalBackground />} />
              <Route path="/endorsements" element={<Endorsements />} />
              <Route path="/widgets" element={<Hangman />} />
              <Route path="/contact" element={<ContactMe />} />
            </Routes>
          </ErrorBoundary>
        </Paper>
        <Footer />
      </ThemeProvider>
    </div>
  );
}

function Fallback({ error }) {
  return (
    <div role="alert">
      <p>I&apos;m so sorry! Something went wrong:</p>
      <pre className="error-fallback">{error.message}</pre>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <PageTracker />
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
